import { useEffect, useState, useCallback } from "react";
import { gql, useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { useAnalytics, Events, useQueryParams } from "@hooks";
import { getOrCreateSessionId, setSessionUserId } from "../utils/sentryTrackingConfig";
import { setLogoutEventsSent } from "../utils/logoutTrackingGuard";
import { useAuthToken } from "./useAuthToken";
import { isAlphaOrProd } from "@src/constants";
import { logger } from '../utils/logger';
import { cleverLoginWithIdpV2, persistIdpV2Tokens } from '../utils/idpV2Auth';

const CleverSSOUserMutationGQL = gql`
  mutation getCleverUser($input: CleverLoginInput!) {
    getCleverUser(input: $input) {
      firstName
      lastName
      email
      accessToken
      id
    }
  }
`;

export type CleverUserResponse = {
  email: string;
  firstName: string;
  lastName: string;
  accessToken: string;
  id?: string;
};

const cleverConfig = {
  clientId: import.meta.env.VITE_CLEVER_CLIENT_ID || "8374a7ed324a2d169e1f",
  districtId: "662ff80eca2429850631906f",
  redirectUri: `${window.origin}/login/`,
};

export default function useCleverAuth(type: "Login" | "Registration") {
  const [cleverAuthLoading, setCleverAuthLoading] = useState(false);
  const [cleverToken, setCleverToken] = useState("");
  const [loginError, setLoginError] = useState<Error>();
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get("returnUrl");
  const { search } = useLocation();
  const [cleverUser, setCleverUser] = useState<{
    accessToken: string;
    email: string;
    firstName: string;
    lastName: string;
  }>();

  const { trackEvent, segment, identify } = useAnalytics();
  const [getCleverUserMutation, { error: cleverUserError }] = useMutation<{
    getCleverUser: CleverUserResponse;
  }>(CleverSSOUserMutationGQL, {
    onCompleted: ({ getCleverUser }) => {
      if (getCleverUser.email) {
        setCleverUser(getCleverUser);
      }
    },
    onError: (error, options) => {
      logger.debug(`[Error]`, error);
    },
  });

  const login = useCallback(
    async (code, redirect_uri) => {
      if (!code || !redirect_uri) return;
      try {
        if (type === "Login") {
          setLoginError(undefined);
          const cleverLogin = await cleverLoginWithIdpV2(code, redirect_uri);
          if (cleverLogin.access_token) {
            setLogoutEventsSent(false);
            removeAuthToken();
            persistIdpV2Tokens(cleverLogin);
            setAuthToken(cleverLogin.access_token, true);
            if (cleverLogin.user_id) {
              segment?.identify(cleverLogin.user_id);
              identify(cleverLogin.user_id, { email: cleverLogin.email });
            }
            const sessionId = getOrCreateSessionId();
            const timestamp = new Date().toISOString();
            const d = { email: cleverLogin.email, loginType: 'Clever', userId: cleverLogin.user_id, sessionId, timestamp };
            trackEvent(Events.actions.logIn, d);
            trackEvent(Events.actions.sessionStarted, d);
            if (cleverLogin.user_id) setSessionUserId(cleverLogin.user_id);
            if (returnUrl && search.replace('?returnUrl=', '').includes(returnUrl)) {
              push(search.replace('?returnUrl=', '').replace(`${returnUrl}&`, `${returnUrl}?`));
            } else {
              push('/');
            }
          } else if (cleverLogin.mfa_required && cleverLogin.challenge_id && (cleverLogin.method === 'totp' || cleverLogin.method === 'totp_setup')) {
            const state: any = { idpChallengeId: cleverLogin.challenge_id, idpMethod: cleverLogin.method, email: cleverLogin.email };
            if (cleverLogin.method === 'totp_setup' && cleverLogin.uri) state.totpUri = cleverLogin.uri;
            push('/verify', state);
          } else if (cleverLogin.mfa_required && cleverLogin.challenge_id && cleverLogin.method === 'email_otp') {
            push('/admin/verify', { idpChallengeId: cleverLogin.challenge_id, idpMethod: cleverLogin.method, email: cleverLogin.email });
          } else {
            throw new Error('Login attempt failed');
          }
        } else {
          await getCleverUserMutation({
            variables: {
              input: {
                code,
                redirect_uri,
              },
            },
          });
        }
      } catch (error) {
        const loginError = error instanceof Error ? error : new Error('Login attempt failed');
        setLoginError(loginError);
        trackEvent(Events.actions.logInFailed, {
          loginType: 'Clever',
          clientId: localStorage.getItem('clientId'),
          message: 'Login attempt failed',
          error: loginError.message,
        });
        logger.error("Login failed:", error);
      } finally {
        setCleverAuthLoading(false);
      }
    },
    [getCleverUserMutation, identify, push, removeAuthToken, returnUrl, search, segment, setAuthToken, trackEvent, type]
  );

  const cleverLogin = () => {
    setCleverAuthLoading(true);
    const url = isAlphaOrProd
      ? `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${cleverConfig.redirectUri}&client_id=${cleverConfig.clientId}`
      : `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${cleverConfig.redirectUri}&client_id=${cleverConfig.clientId}&district_id=${cleverConfig.districtId}`;

    function popupWindow({ url, title, win, w, h }) {
      const y = win.top.outerHeight / 2 + win.top.screenY - h / 2;
      const x = win.top.outerWidth / 2 + win.top.screenX - w / 2;
      return win.open(
        url,
        title,
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
      );
    }
    const popup = popupWindow({
      url,
      title: "Clever SSO",
      win: window,
      w: 483,
      h: 599,
    });
    const checkPopup = setInterval(() => {
      try {
        const code = popup?.window.location?.search.replace("?code=", "");
        if (
          popup?.window?.location?.href.includes(cleverConfig.redirectUri) &&
          code
        ) {
          setCleverToken(code);
          popup.close();
        }
        if (popup.closed) {
          clearInterval(checkPopup);
        }
        if (!popup || !popup.closed) return;
        clearInterval(checkPopup);
      } catch (err) {
        if (popup.closed && !cleverToken) {
          setCleverAuthLoading(false);
        }
        if (popup.closed) {
          clearInterval(checkPopup);
        }
      }
    }, 100);
  };

  useEffect(() => {
    if (cleverToken) {
      login(cleverToken, cleverConfig.redirectUri);
    }
  }, [cleverToken, login]);

  return {
    cleverAuth: cleverLogin,
    cleverAuthLoading,
    cleverUser,
    setCleverUser,
    cleverError: loginError || cleverUserError,
  };
}
